import { useQuery } from "@tanstack/react-query";

import type { AgencySubscription } from "common/types";

import { post } from "../helpers/HTTP";

async function customerEstimateCreationRequest(
  quantity: number,
  planPricingId: string,
): Promise<{
  amountDue: number;
  nextBillingAt: number;
  pricePerItem: number;
  basePrice: number;
}> {
  const { estimate } = await post("subscriptions/estimates/create", {
    seat_count: quantity,
    item_price_id: planPricingId,
  });

  const {
    invoice_estimate: invoiceEstimate,
    subscription_estimate: subscriptionEstimate,
    next_invoice_estimate: nextInvoiceEstimate,
  } = estimate;

  return {
    amountDue: invoiceEstimate
      ? invoiceEstimate.amount_due
      : nextInvoiceEstimate.amount_due,
    nextBillingAt: subscriptionEstimate.next_billing_at,
    pricePerItem: invoiceEstimate.line_items.find(
      ({ pricing_model }: { pricing_model: string }) =>
        pricing_model === "per_unit",
    ).unit_amount,
    basePrice: invoiceEstimate.line_items.find(
      ({ pricing_model }: { pricing_model: string }) =>
        pricing_model === "flat_fee",
    ).unit_amount,
  };
}

export default function useCustomerEstimateCreation({
  subscription,
  planPricingId,
  quantity,
}: {
  planPricingId: string;
  quantity: number;
  subscription: AgencySubscription;
}) {
  const { data: estimateCreation, isLoading: isLoadingEstimateCreation } =
    useQuery({
      queryKey: ["customerEstimateCreation", planPricingId, quantity],
      queryFn: () => customerEstimateCreationRequest(quantity, planPricingId),
      enabled: !subscription || subscription.status === "non_renewing",
    });

  return { estimateCreation, isLoadingEstimateCreation };
}
